import { notifications } from '@mantine/notifications';

import { baseApiUrl } from './config';
import { ApiResponse, RequestMethodEnum } from './types';

const REQUEST_TIMEOUT = 30000; // 30 seconds

const handleUnauthorizedError = () => {
  window.dispatchEvent(new Event('session-expired'));
};

const showErrorNotification = (message: string, showErrorMessage?: boolean) => {
  if (showErrorMessage) {
    notifications.show({
      color: 'red',
      message,
      title: 'Error',
    });
  }
};

export const apiRequest = async <TResponse, TBody = unknown>(
  endpoint: string,
  method: RequestMethodEnum,
  options: {
    showErrorMessage?: boolean;
    showSuccessMessage?: boolean;
    data?: TBody;
    formData?: FormData;
    timeout?: number;
  }
): Promise<TResponse | undefined> => {
  const {
    data,
    formData,
    showErrorMessage,
    showSuccessMessage,
    timeout = REQUEST_TIMEOUT,
  } = options;

  const headers: HeadersInit = formData
    ? {}
    : { 'Content-Type': 'application/json' };

  const body = formData || (data ? JSON.stringify(data) : undefined);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const requestOptions: RequestInit = {
    body,
    credentials: 'include',
    headers,
    method,
    signal: controller.signal,
  };

  const url = `${baseApiUrl}${endpoint}`;

  try {
    const response = await fetch(url, requestOptions);
    clearTimeout(timeoutId);

    if (response.status === 401) {
      handleUnauthorizedError();
      return undefined;
    }

    const contentType = response.headers.get('content-type');

    if (!contentType?.includes('application/json')) {
      if (!response.ok) {
        const errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(errorMessage);
      }
      return undefined;
    }

    const json = (await response.json()) as ApiResponse<TResponse>;

    if (!response.ok) {
      const errorMessage =
        json?.message || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    if (showSuccessMessage && json?.message) {
      notifications.show({
        color: 'green',
        message: json.message,
        title: 'Success',
      });
    }

    return json as TResponse;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        const timeoutError = new Error('Request timeout');
        showErrorNotification(
          'Request timeout. Please try again.',
          showErrorMessage
        );
        throw timeoutError;
      }

      showErrorNotification(
        error.message || 'An unexpected error occurred',
        showErrorMessage
      );
      throw error;
    }

    const unknownError = new Error('Unknown error occurred');
    showErrorNotification('An unexpected error occurred', showErrorMessage);
    throw unknownError;
  }
};
